import React, { useEffect, useRef } from "react";

export default function FootballBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animFrame;
    let W, H;

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // --- Players setup ---
    const makePlayer = (x, y, team, num, skinIdx, hairIdx) => ({
      x, y, homeX: x, homeY: y,
      vx: 0, vy: 0,
      angle: Math.random() * Math.PI * 2,
      team,
      legPhase: Math.random() * Math.PI * 2,
      num, skinIdx, hairIdx,
    });

    const players = [
      // Team A - green (left side)
      makePlayer(0.18, 0.30, "A", "10", 0, 0),
      makePlayer(0.12, 0.60, "A", "7",  1, 1),
      makePlayer(0.32, 0.50, "A", "9",  2, 2),
      makePlayer(0.22, 0.72, "A", "4",  0, 3),
      // Team B - red (right side)
      makePlayer(0.72, 0.28, "B", "11", 3, 0),
      makePlayer(0.82, 0.62, "B", "8",  1, 2),
      makePlayer(0.60, 0.48, "B", "6",  0, 1),
      makePlayer(0.68, 0.72, "B", "3",  2, 3),
    ];

    // Set home as fraction, convert to px
    players.forEach(p => {
      p.homeX = p.x * W;
      p.homeY = p.y * H;
      p.x = p.homeX;
      p.y = p.homeY;
    });

    let ball = { x: players[0].x, y: players[0].y, vx: 0, vy: 0, spin: 0 };
    let ownerIdx = 0;
    let passTarget = -1;
    let passTimer = 0;
    let ballState = "held"; // held | flying

    // --- Drawing helpers ---
    const JERSEY_A = "#16a34a";
    const JERSEY_B = "#ef4444";
    const DARK_A = "#14532d";
    const DARK_B = "#991b1b";
    const SKIN = "#f5c5a3";

    function drawPitch() {
      ctx.fillStyle = "#2a7534";
      ctx.fillRect(0, 0, W, H);

      // Alternating stripes
      const stripeH = 80;
      for (let i = 0; i * stripeH < H; i++) {
        if (i % 2 === 0) {
          ctx.fillStyle = "rgba(0,0,0,0.07)";
          ctx.fillRect(0, i * stripeH, W, stripeH);
        }
      }

      // Center line
      ctx.save();
      ctx.setLineDash([12, 9]);
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, H / 2);
      ctx.lineTo(W, H / 2);
      ctx.stroke();
      ctx.restore();

      // Center circle
      ctx.beginPath();
      ctx.arc(W / 2, H / 2, Math.min(W, H) * 0.13, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255,255,255,0.16)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Center spot
      ctx.beginPath();
      ctx.arc(W / 2, H / 2, 4, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.25)";
      ctx.fill();

      // Penalty areas
      const pw = W * 0.12, ph = H * 0.35;
      ctx.strokeStyle = "rgba(255,255,255,0.14)";
      ctx.lineWidth = 2;
      ctx.strokeRect(0, (H - ph) / 2, pw, ph);
      ctx.strokeRect(W - pw, (H - ph) / 2, pw, ph);

      // Dark overlay for text readability
      ctx.fillStyle = "rgba(0,0,0,0.38)";
      ctx.fillRect(0, 0, W, H);
    }

    function drawPlayer(p) {
      const jersey = p.team === "A" ? JERSEY_A : JERSEY_B;
      const dark = p.team === "A" ? DARK_A : DARK_B;
      const legSwing = Math.sin(p.legPhase) * 8;
      const shorts = p.team === "A" ? "#14532d" : "#7f1d1d";
      const sockColor = p.team === "A" ? "#16a34a" : "#ef4444";
      const skinTones = ["#f5c5a3", "#d4956a", "#c8864e", "#a0522d"];
      const skin = skinTones[p.skinIdx || 0];
      const hairColors = ["#1a0a00", "#3b1f0a", "#2c1810", "#111111"];
      const hair = hairColors[p.hairIdx || 0];

      ctx.save();
      ctx.lineCap = "round";

      // Shadow
      ctx.beginPath();
      ctx.ellipse(p.x, p.y + 22, 10, 3.5, 0, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fill();

      // ---- LEFT LEG ----
      const lLegX = p.x - 4;
      const lLegY = p.y + 11;
      const lFootX = lLegX - legSwing * 0.5;
      const lFootY = lLegY + 12;
      // Thigh
      ctx.strokeStyle = shorts;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(lLegX, lLegY);
      ctx.lineTo(lLegX - legSwing * 0.25, lLegY + 6);
      ctx.stroke();
      // Shin
      ctx.strokeStyle = sockColor;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(lLegX - legSwing * 0.25, lLegY + 6);
      ctx.lineTo(lFootX, lFootY);
      ctx.stroke();
      // Boot
      ctx.fillStyle = "#111";
      ctx.beginPath();
      ctx.ellipse(lFootX, lFootY + 2, 5, 3, -0.2, 0, Math.PI * 2);
      ctx.fill();

      // ---- RIGHT LEG ----
      const rLegX = p.x + 4;
      const rLegY = p.y + 11;
      const rFootX = rLegX + legSwing * 0.5;
      const rFootY = rLegY + 12;
      ctx.strokeStyle = shorts;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(rLegX, rLegY);
      ctx.lineTo(rLegX + legSwing * 0.25, rLegY + 6);
      ctx.stroke();
      ctx.strokeStyle = sockColor;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(rLegX + legSwing * 0.25, rLegY + 6);
      ctx.lineTo(rFootX, rFootY);
      ctx.stroke();
      ctx.fillStyle = "#111";
      ctx.beginPath();
      ctx.ellipse(rFootX, rFootY + 2, 5, 3, 0.2, 0, Math.PI * 2);
      ctx.fill();

      // ---- SHORTS ----
      ctx.fillStyle = shorts;
      ctx.beginPath();
      ctx.roundRect(p.x - 7, p.y + 8, 14, 8, 2);
      ctx.fill();

      // ---- JERSEY BODY ----
      ctx.fillStyle = jersey;
      ctx.beginPath();
      ctx.roundRect(p.x - 9, p.y - 5, 18, 16, 3);
      ctx.fill();
      // Collar
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.beginPath();
      ctx.roundRect(p.x - 4, p.y - 6, 8, 4, 2);
      ctx.fill();
      // Sleeve stripes
      ctx.fillStyle = "rgba(255,255,255,0.2)";
      ctx.fillRect(p.x - 10, p.y - 4, 3, 8);
      ctx.fillRect(p.x + 7, p.y - 4, 3, 8);
      // Jersey number
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.font = "bold 6px Arial";
      ctx.textAlign = "center";
      ctx.fillText(p.num || "7", p.x, p.y + 7);

      // ---- ARMS ----
      ctx.strokeStyle = jersey;
      ctx.lineWidth = 4;
      const armSwing = Math.sin(p.legPhase + Math.PI) * 5;
      ctx.beginPath();
      ctx.moveTo(p.x - 9, p.y - 2);
      ctx.lineTo(p.x - 13, p.y + 4 + armSwing);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(p.x + 9, p.y - 2);
      ctx.lineTo(p.x + 13, p.y + 4 - armSwing);
      ctx.stroke();
      // Hands
      ctx.fillStyle = skin;
      ctx.beginPath();
      ctx.arc(p.x - 13, p.y + 5 + armSwing, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x + 13, p.y + 5 - armSwing, 3, 0, Math.PI * 2);
      ctx.fill();

      // ---- NECK ----
      ctx.fillStyle = skin;
      ctx.beginPath();
      ctx.roundRect(p.x - 3, p.y - 9, 6, 5, 2);
      ctx.fill();

      // ---- HEAD ----
      ctx.beginPath();
      ctx.arc(p.x, p.y - 13, 8, 0, Math.PI * 2);
      ctx.fillStyle = skin;
      ctx.fill();

      // Hair (top arc)
      ctx.beginPath();
      ctx.arc(p.x, p.y - 14, 8, Math.PI * 1.1, Math.PI * 1.9);
      ctx.fillStyle = hair;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x, p.y - 14, 8, Math.PI, Math.PI * 2);
      ctx.strokeStyle = hair;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Eyes
      ctx.fillStyle = "#222";
      ctx.beginPath();
      ctx.arc(p.x - 3, p.y - 13, 1.2, 0, Math.PI * 2);
      ctx.arc(p.x + 3, p.y - 13, 1.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    function drawBall() {
      const r = 8;
      // Shadow
      ctx.beginPath();
      ctx.ellipse(ball.x + 2, ball.y + r + 3, 7, 3, 0, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.fill();

      // Ball
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, r, 0, Math.PI * 2);
      const grad = ctx.createRadialGradient(ball.x - 2, ball.y - 2, 1, ball.x, ball.y, r);
      grad.addColorStop(0, "#ffffff");
      grad.addColorStop(1, "#d4d4d4");
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = "#555";
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // Pentagon pattern (rotates with spin)
      ctx.save();
      ctx.translate(ball.x, ball.y);
      ctx.rotate(ball.spin);
      ctx.fillStyle = "rgba(50,50,50,0.5)";
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        ctx.beginPath();
        ctx.arc(Math.cos(a) * 4, Math.sin(a) * 4, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // --- Game logic ---
    function update() {
      // Update home positions based on current viewport (handles resize)
      players.forEach(p => {
        p.homeX = p.homeX; // already set in px
        // Wander around home
        p.angle += (Math.random() - 0.5) * 0.06;
        const dx = p.homeX - p.x;
        const dy = p.homeY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 70) {
          p.vx += dx * 0.012;
          p.vy += dy * 0.012;
        } else {
          p.vx += Math.cos(p.angle) * 0.35;
          p.vy += Math.sin(p.angle) * 0.35;
        }
        p.vx *= 0.9;
        p.vy *= 0.9;
        p.x = Math.max(20, Math.min(W - 20, p.x + p.vx));
        p.y = Math.max(70, Math.min(H - 50, p.y + p.vy));

        // Animate legs when moving
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        p.legPhase += speed * 0.4;
      });

      if (ballState === "held") {
        const owner = players[ownerIdx];
        ball.x = owner.x + 10;
        ball.y = owner.y + 12;
        ball.spin += 0.02;

        passTimer++;
        const delay = 80 + Math.random() * 80;
        if (passTimer > delay) {
          // Pick a teammate
          const team = owner.team;
          const mates = players
            .map((p, i) => ({ p, i }))
            .filter(({ p, i }) => p.team === team && i !== ownerIdx);
          if (mates.length > 0) {
            const pick = mates[Math.floor(Math.random() * mates.length)];
            passTarget = pick.i;
            const dx = pick.p.x - ball.x;
            const dy = pick.p.y - ball.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            const spd = 6 + Math.random() * 4;
            ball.vx = (dx / d) * spd;
            ball.vy = (dy / d) * spd;
            ballState = "flying";
            passTimer = 0;
          }
        }
      } else if (ballState === "flying") {
        ball.x += ball.vx;
        ball.y += ball.vy;
        ball.vx *= 0.975;
        ball.vy *= 0.975;
        ball.spin += 0.18;

        // Bounce off viewport edges — always stays visible
        if (ball.x < 15) { ball.x = 15; ball.vx = Math.abs(ball.vx) * 0.7; }
        if (ball.x > W - 15) { ball.x = W - 15; ball.vx = -Math.abs(ball.vx) * 0.7; }
        if (ball.y < 60) { ball.y = 60; ball.vy = Math.abs(ball.vy) * 0.7; }
        if (ball.y > H - 40) { ball.y = H - 40; ball.vy = -Math.abs(ball.vy) * 0.7; }

        // Target received?
        const target = players[passTarget];
        const dx = target.x - ball.x;
        const dy = target.y - ball.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const slowEnough = Math.abs(ball.vx) < 0.8 && Math.abs(ball.vy) < 0.8;

        if (dist < 22 || slowEnough) {
          ownerIdx = passTarget;
          ballState = "held";
          passTimer = 0;
        }

        // Interception by opponent
        players.forEach((p, i) => {
          if (p.team !== players[ownerIdx].team) {
            const dx2 = p.x - ball.x;
            const dy2 = p.y - ball.y;
            if (Math.sqrt(dx2 * dx2 + dy2 * dy2) < 20) {
              ownerIdx = i;
              ballState = "held";
              passTimer = 0;
            }
          }
        });
      }
    }

    function loop() {
      ctx.clearRect(0, 0, W, H);
      drawPitch();
      players.forEach(drawPlayer);
      drawBall();
      update();
      animFrame = requestAnimationFrame(loop);
    }

    loop();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}